import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, FileImage, FileText, Loader2, Sparkles } from "lucide-react";
import jsPDF from "jspdf";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { getFlyerForRegion } from "@/lib/conferencePublic";

interface Props {
  region?: string | null;
  regionId?: string | null;
}

const slugify = (s: string) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export const RegionalFlyerDownload: React.FC<Props> = ({ region, regionId }) => {
  const [busy, setBusy] = useState<"jpg" | "pdf" | null>(null);
  const [flyerUrl, setFlyerUrl] = useState("");
  const safeRegion = region || "";
  const base = `ofuesi-${slugify(safeRegion)}-2026-flyer`;

  useEffect(() => {
    let cancelled = false;

    const loadFlyerUrl = async () => {
      if (!region) {
        if (!cancelled) setFlyerUrl("");
        return;
      }

      const fallback = getFlyerForRegion(region);
      const resolvedRegionId = regionId || (
        await supabase
          .from("regions")
          .select("id")
          .ilike("name", region.trim())
          .limit(1)
          .maybeSingle()
      ).data?.id;

      if (!resolvedRegionId) {
        if (!cancelled) setFlyerUrl(fallback);
        return;
      }
      const [{ data: contentRow }, { data: conferenceRow }, { data: kitRow }] = await Promise.all([
        (supabase.from("conference_content" as any) as any)
          .select("data")
          .eq("region_id", resolvedRegionId)
          .eq("section", "flyer")
          .eq("conference_slug", "regional-conference-2026")
          .eq("is_active", true)
          .order("updated_at", { ascending: false })
          .limit(1)
          .maybeSingle(),
        supabase
          .from("conferences")
          .select("flyer_url")
          .eq("region_id", resolvedRegionId)
          .eq("is_active", true)
          .eq("status", "upcoming")
          .ilike("conference_name", "%2026%")
          .limit(1)
          .maybeSingle(),
        (supabase.from("conference_kit_events" as any) as any)
          .select("flyer_url")
          .eq("region_id", resolvedRegionId)
          .eq("conference_year", 2026)
          .limit(1)
          .maybeSingle(),
      ]);

      const nextUrl =
        (contentRow as any)?.data?.image_url ||
        (contentRow as any)?.data?.url ||
        (conferenceRow as any)?.flyer_url ||
        (kitRow as any)?.flyer_url ||
        fallback;

      if (!cancelled) setFlyerUrl(nextUrl);
    };

    void loadFlyerUrl();
    return () => {
      cancelled = true;
    };
  }, [region, regionId]);

  if (!region) return null;

  const downloadJpg = async () => {
    try {
      setBusy("jpg");
      const resp = await fetch(flyerUrl);
      if (!resp.ok) throw new Error("Flyer not found");
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${base}.jpg`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast({ title: "JPEG downloaded" });
    } catch (e: any) {
      toast({ title: "Download failed", description: e.message, variant: "destructive" });
    } finally {
      setBusy(null);
    }
  };

  const downloadPdf = async () => {
    try {
      setBusy("pdf");
      const resp = await fetch(flyerUrl);
      const blob = await resp.blob();
      const dataUrl: string = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result as string);
        r.onerror = rej;
        r.readAsDataURL(blob);
      });
      const img = new Image();
      img.src = dataUrl;
      await new Promise((res) => { img.onload = res; });
      const pdf = new jsPDF({ unit: "pt", format: [img.width, img.height], orientation: img.width > img.height ? "landscape" : "portrait" });
      pdf.addImage(dataUrl, "JPEG", 0, 0, img.width, img.height);
      pdf.save(`${base}.pdf`);
      toast({ title: "PDF downloaded" });
    } catch (e: any) {
      toast({ title: "Download failed", description: e.message, variant: "destructive" });
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card className="border-sky-200 bg-gradient-to-br from-sky-50 to-amber-50">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-amber-600" />
          Published Flyer — {region}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col md:flex-row gap-4 items-start">
          <img
            src={flyerUrl}
            alt={`${region} 2026 flyer`}
            className="w-40 rounded-md border shadow-sm shrink-0"
          />
          <div className="flex-1 space-y-3">
            <p className="text-sm text-muted-foreground">
              Download the currently published flyer for this region to share on socials, WhatsApp, or print.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button onClick={downloadJpg} disabled={busy !== null} size="sm" variant="outline">
                {busy === "jpg" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileImage className="w-4 h-4 mr-2" />}
                Download JPEG
              </Button>
              <Button onClick={downloadPdf} disabled={busy !== null} size="sm" variant="outline">
                {busy === "pdf" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                Download PDF
              </Button>
              <a href={flyerUrl} target="_blank" rel="noreferrer">
                <Button size="sm" variant="ghost">
                  <Download className="w-4 h-4 mr-2" /> Open in new tab
                </Button>
              </a>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default RegionalFlyerDownload;
